/** Service Desk console: the catalogue (products, sync, live price test, visibility) (SD-S2-21, WP-S2-H). */
export const serviceCatalogue = {
  title: "Catalogue",
  subtitle: "Products, SKUs, stock and prices cached from your order system. Your system stays the source of truth; Sagot never invents a price.",
  profileLink: "Commerce profile",
  error: { title: "We couldn't load the catalogue", body: "Your order system is not affected. Try again." },
  banner: {
    paused: "Ordering is paused. Browsing shows the cached items; clients can't order until you resume.",
    unhealthy: "The connector is unhealthy. Browsing uses the cached items; live prices and stock checks are unavailable.",
    settings: "Commerce profile",
  },
  tabs: { label: "Catalogue sections", products: "Products", visibility: "Visibility" },
  stats: {
    label: "Catalogue totals",
    active: "Active items",
    activeHint: "that clients can find",
    categories: "Categories",
    categoriesHint: "in the catalogue",
    low: "Low stock",
    lowHint: "at or under the low-stock level",
    made: "Made to order",
    madeHint: "lead time instead of stock",
  },
  stock: { unknown: "Stock not known", in_stock: "In stock", low: "Low stock", out: "Out of stock", made_to_order: "Made to order" },
  sync: {
    title: "Synced from {{name}}",
    csvTitle: "Catalogue from a CSV file",
    system: "your order system",
    healthy: "Healthy",
    unhealthy: "Unhealthy",
    paused: "Paused",
    noProfile: "No commerce profile",
    last: "Last sync",
    lastValue: "{{when}} · {{items}} items read · {{changed}} changed",
    never: "Not synced yet",
    running: "Syncing now…",
    next: "Next sync",
    nextValue: "Nightly at {{at}}, hourly changes every {{minutes}} minutes, order statuses every {{poll}} minutes.",
    lastFailed: "The last sync failed. The cached items below are from the sync before it.",
    historyFailed: "The sync history could not be loaded.",
    now: "Sync now",
    started: "Sync started.",
    joined: "A sync is already running. You'll see its result here.",
    history: "Sync history",
    csvInstead: "Upload a CSV instead",
    uploadCsv: "Upload a products CSV",
  },
  filters: {
    search: "Search SKU, product, category or model",
    searchLabel: "Search the catalogue",
    category: "Category",
    allCategories: "All categories",
    stockGroup: "Stock filter",
    allStock: "All stock",
    activeOnly: "Active only",
    clear: "Clear filters",
  },
  list: {
    label: "Catalogue",
    product: "Product",
    category: "Category",
    unit: "Unit",
    price: "List price",
    stock: "Stock",
    lead: "Lead time",
    days_one: "{{count}} day",
    days_other: "{{count}} days",
    hidden: "Hidden from ordering",
    open: "Open {{name}}",
    showing: "Showing {{shown}} of {{total}} items",
  },
  empty: {
    title: "No products yet",
    body: "Connect your order system to sync the catalogue nightly, or upload a CSV if you don't have an API.",
    setup: "Set up the commerce profile",
    filteredTitle: "No products match",
    filteredBody: "Try another search or clear the filters. Models are searched too.",
  },
  drawer: {
    loading: "Product",
    notFoundTitle: "We can't find this product",
    notFoundBody: "It may have been archived. Go back to the list.",
    archived: "Archived",
    notOrderable: "Hidden from ordering",
    cached: "Cached data",
    sku: "SKU",
    category: "Category",
    unit: "Unit",
    listPrice: "List price",
    vatIn: "(VAT included)",
    vatEx: "(VAT excluded)",
    stock: "Stock",
    lead: "Lead time",
    minQty: "Minimum order / step",
    synced: "Last synced",
    source: { sync: "From your order system", csv: "From a CSV", manual: "Added by hand" },
    compatible: "Compatible with",
    availability: "Availability",
    orderable: "Available to order in chat",
    orderableHint: "Hide a product from ordering without removing it from your system.",
    orderableOn: "Available to order again.",
    orderableOff: "Hidden from ordering.",
    visible: "Visible to clients",
    visibleHint: "Turn off to keep it out of search and product cards.",
    visibleOn: "Visible to clients again.",
    visibleOff: "Hidden from clients.",
    raw: "Mapped fields",
  },
  live: {
    title: "Test live price",
    customer: "Customer code",
    customerHint: "The customer code your system knows the organisation by.",
    qty: "Quantity",
    run: "Test live price",
    limit: "Calls your price operation once. Limited to 10 tests a minute.",
    ok: "{{status}} OK",
    failed: "The call failed",
    duration: "{{ms}} ms",
    canonical: "Canonical result",
    noMapping: "The call worked, but the mapping returned no fields. Check the price operation's mapping.",
    problem: { customer: "Enter a customer code.", qty: "Enter a whole quantity from 1 to {{max}}." },
    unavailable: {
      noAccess: "You need permission to test connector operations to try a live price.",
      noConnector: "No connector is set up for the commerce profile yet.",
      noBinding: "Bind a get_price operation in the commerce profile to test live prices.",
    },
  },
  history: {
    title: "Sync history",
    description: "Nightly and manual syncs, newest first.",
    label: "Catalogue syncs",
    when: "When",
    mode: "Mode",
    items: "Items",
    changes: "Changes",
    result: "Result",
    kind: { full: "Full", incremental: "Changes only", csv: "CSV" },
    origin: { schedule: "scheduled", manual: "manual" },
    status: { queued: "Queued", running: "Running", succeeded: "Succeeded", partial: "Partly failed", failed: "Failed" },
    truncated: "Stopped at the page limit: part of the catalogue was not read.",
    emptyTitle: "No syncs yet",
    emptyBody: "The first sync runs when the commerce profile is ready, or press Sync now.",
  },
  visibility: {
    title: "Catalogue visibility",
    intro: "Choose what each client organisation can see. Without a rule an organisation sees the whole catalogue.",
    organisation: "Organisation",
    rule: "Can see",
    action: "Action",
    edit: "Edit",
    editFor: "Edit what {{name}} can see",
    summaryAll: "Everything",
    summaryCategories_one: "{{count}} category",
    summaryCategories_other: "{{count}} categories",
    summarySkus_one: "{{count}} product",
    summarySkus_other: "{{count}} products",
    emptyTitle: "No organisations yet",
    emptyBody: "Add client organisations first; then choose what each can see.",
    dialogTitle: "What {{name}} can see",
    dialogBody: "The rule applies to search, product cards and ordering for this organisation.",
    mode: "Visibility",
    modes: { all: "Everything", categories: "Some categories", allow_list: "Only listed products" },
    modeHints: { all: "The whole active catalogue.", categories: "Products in the categories you tick.", allow_list: "Only the SKUs you list." },
    categories: "Categories",
    noCategories: "The catalogue has no categories yet.",
    skus: "SKUs to allow",
    skusHint: "Paste SKUs separated by commas, spaces or new lines.",
    addSkus: "Add SKUs",
    skuList: "Allowed SKUs",
    removeSku: "Remove {{sku}}",
    problem: { categories: "Tick at least one category.", skus: "List at least one SKU.", tooMany: "That is more than the rule can hold." },
    save: "Save rule",
    saved: "{{name}}'s rule was saved.",
    reset: "Back to everything",
    cleared: "{{name}} can see everything again.",
  },
} as const;
